import React, { useRef, useMemo, useLayoutEffect } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { POS, POOL } from "./layout";
import { makeFacade, antiprismGeometry, makeNames, makeCanopy, makeWaterfall } from "../lib/textures";
import { fadeGroup, wtcOpacity, poolFactor } from "./fade";
import { envState } from "./env";
import { useStore } from "../store";
import { getSeason } from "../lib/season";

// Square plane with a central square hole cut out (for the recessed water surface).
function holedRect(size, holeHalf) {
  const h = size / 2;
  const shape = new THREE.Shape();
  shape.moveTo(-h, -h);
  shape.lineTo(h, -h);
  shape.lineTo(h, h);
  shape.lineTo(-h, h);
  shape.lineTo(-h, -h);
  const hole = new THREE.Path();
  hole.moveTo(-holeHalf, -holeHalf);
  hole.lineTo(holeHalf, -holeHalf);
  hole.lineTo(holeHalf, holeHalf);
  hole.lineTo(-holeHalf, holeHalf);
  hole.lineTo(-holeHalf, -holeHalf);
  shape.holes.push(hole);
  return new THREE.ShapeGeometry(shape);
}

const DEPTH = 9; // ~30 ft below grade — first-tier reflecting water
const VOID_DEPTH = 20; // deeper central "bottomless" void

// A true below-grade "Reflecting Absence" pool: the plaza ground has a physical
// hole (see Plaza.jsx) and this geometry descends into it.
function MemorialPool({ pos }) {
  const s = 76;
  const half = s / 2; // 38 — matches the plaza opening
  const voidHalf = 11; // central square void
  const names = useMemo(() => makeNames(), []);
  const fall = useMemo(() => makeWaterfall(), []);
  const waterGeo = useMemo(() => holedRect(s - 2, voidHalf), []);
  const bronzeMat = useMemo(
    () => new THREE.MeshStandardMaterial({ map: names, color: "#2e2620", metalness: 0.72, roughness: 0.5, emissive: new THREE.Color("#fff2d6"), emissiveIntensity: 0 }),
    [names]
  );
  useFrame((_, delta) => {
    if (fall) fall.offset.y -= delta * 0.9; // waterfall sheeting scroll
    bronzeMat.emissiveIntensity = envState.night * 0.55;
  });

  const rimW = 5;
  const rimMid = half + rimW / 2;
  const rimBars = [
    { p: [0, 0.7, rimMid], a: [s + 2 * rimW, 1.4, rimW] },
    { p: [0, 0.7, -rimMid], a: [s + 2 * rimW, 1.4, rimW] },
    { p: [rimMid, 0.7, 0], a: [rimW, 1.4, s] },
    { p: [-rimMid, 0.7, 0], a: [rimW, 1.4, s] },
  ];
  // Sheeting waterfall walls: grade (y=0) -> water surface (y=-DEPTH).
  const walls = [
    { p: [0, -DEPTH / 2, half], r: [0, Math.PI, 0] },
    { p: [0, -DEPTH / 2, -half], r: [0, 0, 0] },
    { p: [half, -DEPTH / 2, 0], r: [0, -Math.PI / 2, 0] },
    { p: [-half, -DEPTH / 2, 0], r: [0, Math.PI / 2, 0] },
  ];
  const vMidY = -(DEPTH + (VOID_DEPTH - DEPTH) / 2);
  const voidWalls = [
    { p: [0, vMidY, voidHalf], r: [0, Math.PI, 0] },
    { p: [0, vMidY, -voidHalf], r: [0, 0, 0] },
    { p: [voidHalf, vMidY, 0], r: [0, -Math.PI / 2, 0] },
    { p: [-voidHalf, vMidY, 0], r: [0, Math.PI / 2, 0] },
  ];
  return (
    <group position={pos}>
      {/* polished absolute-black granite coping flush at grade around the opening */}
      {rimBars.map((b, i) => (
        <mesh key={`c${i}`} position={[b.p[0], 0.16, b.p[2]]} receiveShadow>
          <boxGeometry args={[b.a[0], 0.3, b.a[2]]} />
          <meshStandardMaterial color="#0b0b0d" roughness={0.35} metalness={0.15} />
        </mesh>
      ))}
      {/* oil-rubbed bronze parapet with engraved names */}
      {rimBars.map((b, i) => (
        <mesh key={`r${i}`} position={b.p} receiveShadow castShadow material={bronzeMat}>
          <boxGeometry args={b.a} />
        </mesh>
      ))}
      {/* descending waterfall sheet walls */}
      {walls.map((w, i) => (
        <mesh key={`w${i}`} position={w.p} rotation={w.r}>
          <planeGeometry args={[s, DEPTH]} />
          <meshStandardMaterial
            map={fall}
            color="#0c1417"
            emissive="#4f8f9c"
            emissiveIntensity={0.4}
            roughness={0.35}
            metalness={0.1}
            side={THREE.DoubleSide}
          />
        </mesh>
      ))}
      {/* recessed dark reflecting water with the central void cut out */}
      <mesh geometry={waterGeo} position={[0, -DEPTH + 0.6, 0]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
        <meshStandardMaterial color="#0e1518" metalness={0.4} roughness={0.12} envMapIntensity={0.6} emissive="#0c2a30" emissiveIntensity={0.28} side={THREE.DoubleSide} />
      </mesh>
      {/* inner void walls dropping into a bottomless black core */}
      {voidWalls.map((w, i) => (
        <mesh key={`v${i}`} position={w.p} rotation={w.r}>
          <planeGeometry args={[voidHalf * 2, VOID_DEPTH - DEPTH]} />
          <meshStandardMaterial color="#050708" roughness={0.5} metalness={0.2} side={THREE.DoubleSide} />
        </mesh>
      ))}
      {/* black void floor */}
      <mesh position={[0, -VOID_DEPTH, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[voidHalf * 2, voidHalf * 2]} />
        <meshBasicMaterial color="#000000" />
      </mesh>
    </group>
  );
}

function Grove() {
  const trunkRef = useRef();
  const canopyRef = useRef();
  const crownRef = useRef();
  const season = useMemo(() => getSeason(), []);
  const leaf = useMemo(() => makeCanopy(), []);
  const trees = useMemo(() => {
    const arr = [];
    let s = 4412;
    const rnd = () => ((s = (s * 1103515245 + 12345) % 2147483648) / 2147483648);
    for (let i = 0; i < 46; i++) {
      const x = -70 + rnd() * 150;
      const z = -70 + rnd() * 150;
      // avoid pools footprints
      const dN = Math.hypot(x - POS.northPool[0], z - POS.northPool[2]);
      const dS = Math.hypot(x - POS.southPool[0], z - POS.southPool[2]);
      if (dN < 52 || dS < 52) continue;
      arr.push({ x, z, sc: 0.8 + rnd() * 0.6 });
    }
    return arr;
  }, []);
  useLayoutEffect(() => {
    const m = new THREE.Matrix4();
    const q = new THREE.Quaternion();
    const col = new THREE.Color();
    const GREENS = ["#3f6b2e", "#4c7a34", "#345c28", "#57853c", "#2e5322"];
    trees.forEach((t, i) => {
      m.compose(new THREE.Vector3(t.x, 4 * t.sc, t.z), q, new THREE.Vector3(t.sc, t.sc, t.sc));
      trunkRef.current?.setMatrixAt(i, m);
      q.setFromEuler(new THREE.Euler(0, i * 1.3, 0));
      m.compose(new THREE.Vector3(t.x, 9 * t.sc, t.z), q, new THREE.Vector3(4.6 * t.sc, 5.3 * t.sc, 4.6 * t.sc));
      canopyRef.current?.setMatrixAt(i, m);
      m.compose(new THREE.Vector3(t.x + t.sc, 12 * t.sc, t.z - t.sc), q, new THREE.Vector3(3 * t.sc, 3.3 * t.sc, 3 * t.sc));
      crownRef.current?.setMatrixAt(i, m);
      col.set(GREENS[i % GREENS.length]);
      canopyRef.current?.setColorAt(i, col);
      crownRef.current?.setColorAt(i, col);
      q.identity();
    });
    [trunkRef, canopyRef, crownRef].forEach((r) => {
      if (r.current) r.current.instanceMatrix.needsUpdate = true;
      if (r.current?.instanceColor) r.current.instanceColor.needsUpdate = true;
    });
  }, [trees]);
  return (
    <group>
      <instancedMesh ref={trunkRef} args={[null, null, trees.length]} castShadow>
        <cylinderGeometry args={[0.45, 0.7, 8, 6]} />
        <meshStandardMaterial color="#4f3f28" roughness={0.95} />
      </instancedMesh>
      {!season.bare && (
        <>
          <instancedMesh ref={canopyRef} args={[null, null, trees.length]} castShadow>
            <icosahedronGeometry args={[1, 1]} />
            <meshStandardMaterial map={leaf} roughness={0.85} flatShading />
          </instancedMesh>
          <instancedMesh ref={crownRef} args={[null, null, trees.length]} castShadow>
            <icosahedronGeometry args={[1, 1]} />
            <meshStandardMaterial map={leaf} roughness={0.85} flatShading />
          </instancedMesh>
        </>
      )}
    </group>
  );
}

function SurvivorTree() {
  const season = useMemo(() => getSeason(), []);
  const leaf = useMemo(() => makeCanopy(), []);
  return (
    <group position={[-8, 0, 14]}>
      <mesh position={[0, 6, 0]} castShadow>
        <cylinderGeometry args={[0.7, 1.1, 12, 8]} />
        <meshStandardMaterial color="#5a4a30" roughness={0.9} />
      </mesh>
      {!season.bare && (
        <group position={[0, 13, 0]}>
          <mesh castShadow>
            <icosahedronGeometry args={[5.2, 1]} />
            <meshStandardMaterial map={leaf} color="#79a552" roughness={0.85} flatShading />
          </mesh>
          <mesh position={[2.6, 3, -1.6]} castShadow>
            <icosahedronGeometry args={[3.2, 1]} />
            <meshStandardMaterial map={leaf} color="#6f9e4a" roughness={0.85} flatShading />
          </mesh>
          <mesh position={[-2.8, 2.2, 2.2]} castShadow>
            <icosahedronGeometry args={[3.0, 1]} />
            <meshStandardMaterial map={leaf} color="#8ab55f" roughness={0.85} flatShading />
          </mesh>
        </group>
      )}
      {/* commemorative gold ring */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.12, 0]}>
        <ringGeometry args={[3.4, 4, 40]} />
        <meshStandardMaterial color="#D4AF37" emissive="#8a6a1a" emissiveIntensity={0.4} side={THREE.DoubleSide} />
      </mesh>
    </group>
  );
}

function Oculus() {
  const finRef = useRef();
  const fins = 26;
  useLayoutEffect(() => {
    const m = new THREE.Matrix4();
    const q = new THREE.Quaternion();
    const up = new THREE.Vector3(0, 0, 1);
    for (let i = 0; i < fins; i++) {
      const f = (i / (fins - 1) - 0.5) * 2;
      const x = f * 55;
      const h = 26 * (1 - Math.abs(f) * 0.55);
      const tilt = f * 0.6;
      q.setFromAxisAngle(up, -tilt);
      m.compose(new THREE.Vector3(x, h / 2 + 2, 0), q, new THREE.Vector3(1, h, 3.4));
      finRef.current?.setMatrixAt(i, m);
    }
    if (finRef.current) finRef.current.instanceMatrix.needsUpdate = true;
  }, []);
  return (
    <group position={POS.oculus}>
      <mesh position={[0, 2, 0]} scale={[62, 6, 22]}>
        <sphereGeometry args={[1, 24, 16]} />
        <meshStandardMaterial color="#eef1f4" roughness={0.5} metalness={0.1} />
      </mesh>
      <instancedMesh ref={finRef} args={[null, null, fins]} castShadow>
        <boxGeometry args={[1.4, 1, 1]} />
        <meshStandardMaterial color="#f4f6f8" roughness={0.45} metalness={0.15} />
      </instancedMesh>
    </group>
  );
}

// NOTE: Oculus lives here only (single instance) — the duplicate in WTCComplex was removed.
export default function OneWTC() {
  const tower = useRef();
  const memorial = useRef();
  const glassMat = useRef();

  const towerGeo = useMemo(() => antiprismGeometry(47, 31, 417), []);
  const glass = useMemo(() => {
    const { map, emissiveMap } = makeFacade("glass");
    map.repeat.set(2, 6);
    emissiveMap.repeat.set(2, 6);
    return { map, emissiveMap };
  }, []);

  useFrame(() => {
    const t = useStore.getState().temporal;
    fadeGroup(tower.current, wtcOpacity(t));
    fadeGroup(memorial.current, poolFactor(t));
    if (glassMat.current) glassMat.current.emissiveIntensity = 0.06 + envState.night * 1.1;
  });

  return (
    <>
      <group ref={tower}>
        <group position={POS.oneWTC}>
          <mesh geometry={towerGeo} castShadow receiveShadow>
            <meshStandardMaterial
              ref={glassMat}
              map={glass.map}
              emissiveMap={glass.emissiveMap}
              emissive="#bfe0ff"
              emissiveIntensity={0}
              color="#c4dcec"
              metalness={0.34}
              roughness={0.2}
              envMapIntensity={1.2}
              side={THREE.DoubleSide}
            />
          </mesh>
          {/* parapet + mast */}
          <mesh position={[0, 420, 0]}>
            <boxGeometry args={[20, 6, 20]} />
            <meshStandardMaterial color="#9fb4c4" metalness={0.7} roughness={0.3} />
          </mesh>
          <mesh position={[0, 480, 0]}>
            <cylinderGeometry args={[0.5, 1.2, 124, 12]} />
            <meshStandardMaterial color="#c9d3db" metalness={0.85} roughness={0.25} />
          </mesh>
        </group>
        <Oculus />
      </group>

      <group ref={memorial}>
        <MemorialPool pos={POS.northPool} />
        <MemorialPool pos={POS.southPool} />
        <Grove />
        <SurvivorTree />
      </group>
    </>
  );
}
